import React from "react";
import { ReactComponent as UserIcon } from "assets/icons/user.svg";
import { ReactComponent as UserGroupIcon } from "assets/icons/user-group.svg";
import { ReactComponent as PawIcon } from "assets/icons/paw.svg";
import Card from "components/Card/Card";
import Page from "components/Page/Page";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { readOrgDashboard } from "services/dashboard.service";

const Dashboard = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { orgId, loading: orgLoading } = useActiveOrg();
  const [summary, setSummary] = React.useState<{
    serviceOrderCount?: number;
    paymentCount?: number;
    outstandingInvoiceCount?: number;
  } | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!isAuthenticated || orgLoading || orgId <= 0) {
        setSummary(null);
        setError(null);
        return;
      }
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const { data, error: apiError } = await readOrgDashboard(token, orgId);
        if (cancelled) return;
        if (apiError) {
          setError(apiError.message);
          setSummary(null);
          return;
        }
        setSummary(data ?? null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated, orgId, orgLoading]);

  const soc = summary?.serviceOrderCount;

  return (
    <Page>
      {error && (
        <div className="mb-4 text-sm text-red-500 px-2">
          Dashboard data: {error}
        </div>
      )}
      {/* Stacks below lg; the two columns only sit side by side when there is room. */}
      <div className="flex flex-col gap-6 lg:flex-row">
        {/*
          This card used to render a CircularProgressbar. It was titled "Total
          Appointments" but plotted service orders, and the percentage was
          (count % 1000) / 1000 - which is not a proportion of anything - falling
          back to a hardcoded 0.7 whenever the API returned nothing. Since the
          API exposes no denominator to make a ratio out of, showing the count
          plainly is the honest version.
        */}
        <Card title="Service orders" className="lg:basis-1/3">
          <div className="m-auto text-center my-10">
            <div className="text-4xl font-semibold text-slate-900 sm:text-5xl">
              {soc ?? "—"}
            </div>
            <div className="text-sm text-slate-500 mt-2">
              for your active clinic
            </div>
          </div>
          <p className="p-4 text-sm text-gray-500">
            Loaded from the organisation dashboard API for your active clinic.
          </p>
        </Card>

        <div className="flex min-w-0 flex-col gap-6 lg:basis-2/3">
          {/* Grid rather than flex-basis: one column on phones, three from sm up. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex min-w-0 items-center rounded-[10px] border border-slate-200 bg-white p-4">
              <div className="mr-4 shrink-0 rounded-md bg-vg-cherry/10 p-3 text-vg-cherry">
                <UserIcon className="w-7 h-7" />
              </div>
              <div className="min-w-0">
                <h3 className="truncate text-xs font-medium uppercase tracking-wide text-slate-500">Outstanding invoices</h3>
                <p className="text-2xl font-semibold text-slate-900 mt-0.5">
                  {summary?.outstandingInvoiceCount ?? "—"}
                </p>
              </div>
            </div>

            <div className="flex min-w-0 items-center rounded-[10px] border border-slate-200 bg-white p-4">
              <div className="mr-4 shrink-0 rounded-md bg-vg-azure/15 p-3 text-vg-azure">
                <UserGroupIcon className="w-7 h-7" />
              </div>
              <div className="min-w-0">
                <h3 className="truncate text-xs font-medium uppercase tracking-wide text-slate-500">Payments</h3>
                <p className="text-2xl font-semibold text-slate-900 mt-0.5">
                  {summary?.paymentCount ?? "—"}
                </p>
              </div>
            </div>

            <div className="flex min-w-0 items-center rounded-[10px] border border-slate-200 bg-white p-4">
              <div className="mr-4 shrink-0 rounded-md bg-vg-orange/15 p-3 text-vg-orange">
                <PawIcon className="w-7 h-7" />
              </div>
              <div className="min-w-0">
                <h3 className="truncate text-xs font-medium uppercase tracking-wide text-slate-500">Service orders</h3>
                <p className="text-2xl font-semibold text-slate-900 mt-0.5">
                  {summary?.serviceOrderCount ?? "—"}
                </p>
              </div>
            </div>
          </div>

          <Card title="Welcome to VetGeo" className="grow">
            <p className="p-6 text-gray-500">
              Use the navigation on the left to manage your clients, patients and
              their medical records, your clinic's services, and your staff.
            </p>
          </Card>
        </div>
      </div>
    </Page>
  );
};

export default Dashboard;
